import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Users, Building2, Info, MapPin, Loader2, Phone } from "lucide-react";
import { motion } from "framer-motion";
import Seo, { buildBreadcrumbLd, buildFaqLd, buildLocalServiceLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { api } from "../lib/api";

export default function CityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound

  useEffect(() => {
    let active = true;
    setState("loading");
    api
      .get(`/cities/published/${slug}`)
      .then((r) => {
        if (!active) return;
        setCity(r.data);
        setState("ready");
      })
      .catch(() => active && setState("notfound"));
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (state === "notfound") navigate("/einsatzgebiete", { replace: true });
  }, [state, navigate]);

  if (state !== "ready" || !city) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="city-loading">
        <Loader2 className="h-8 w-8 animate-spin text-yellow" />
      </div>
    );
  }

  const crumbs = [
    { label: "Startseite", to: "/" },
    { label: "Einsatzgebiete", to: "/einsatzgebiete" },
    { label: city.name, to: `/entruempelung/${city.slug}` },
  ];
  const bodyParagraphs = (city.body || "").split(/\n{2,}|\r\n\r\n/).filter(Boolean);
  const faq = (city.faq || []).map((f) => ({ q: f.q, a: f.a }));
  const projects = city.projects || [];

  return (
    <div data-testid={`page-city-${city.slug}`}>
      <Seo
        path={`/entruempelung/${city.slug}`}
        title={city.meta_title}
        description={city.meta_description}
        noindex={city.status === "Noindex"}
        jsonLd={[
          buildBreadcrumbLd(crumbs),
          buildLocalServiceLd(city, `/entruempelung/${city.slug}`),
          ...(faq.length ? [buildFaqLd(faq)] : []),
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero
        kicker={`Einsatzgebiet · ${city.district || city.name}`}
        title={city.h1 || `Entrümpelung in ${city.name}`}
        lead={city.intro}
      />

      {/* Ort-Fakten */}
      <section className="border-b border-light-gray bg-white py-6">
        <div className="container-page flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-mid-gray">
          {city.postal_codes?.length > 0 && (
            <span className="inline-flex items-center gap-2" data-testid="city-plz">
              <MapPin className="h-4 w-4 text-yellow" /> PLZ {city.postal_codes.join(", ")}
            </span>
          )}
          {city.district && <span data-testid="city-district">Landkreis: {city.district}</span>}
          {city.state && <span>{city.state}</span>}
          {city.distance_km != null && (
            <span>ca. {city.distance_km} km vom Sitz Altenstadt</span>
          )}
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="container-page grid gap-14 lg:grid-cols-[1.3fr_0.7fr] lg:gap-20">
          <Reveal>
            {bodyParagraphs.length > 0 && (
              <div className="space-y-4" data-testid="city-body">
                {bodyParagraphs.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-mid-gray">
                    {p}
                  </p>
                ))}
              </div>
            )}

            {city.highlights?.length > 0 && (
              <>
                <h2 className="mt-10 font-heading text-2xl font-bold text-anthracite md:text-3xl">
                  Ihr Einsatz in {city.name}
                </h2>
                <StaggerGroup className="mt-6 space-y-3">
                  {city.highlights.map((h) => (
                    <motion.div key={h} variants={itemVariants} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-yellow text-brand-black">
                        <Check className="h-4 w-4" />
                      </span>
                      <span className="text-base text-mid-gray">{h}</span>
                    </motion.div>
                  ))}
                </StaggerGroup>
              </>
            )}

            <div className="mt-8 flex items-start gap-3 rounded-sm bg-light-gray p-5">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-mid-gray" />
              <p className="text-sm text-mid-gray">
                Rümpeltaxi unterhält in {city.name} keine eigene Niederlassung. Unser Sitz ist in
                Altenstadt – Einsätze in {city.name} und Umgebung führen wir zuverlässig und nach
                individueller Terminabstimmung durch.
              </p>
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {city.audiences?.length > 0 && (
                <div>
                  <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                    Typische Zielgruppen
                  </h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-anthracite">
                    {city.audiences.map((a) => (
                      <li key={a}>· {a}</li>
                    ))}
                  </ul>
                </div>
              )}
              {city.object_types?.length > 0 && (
                <div>
                  <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                    Typische Objektarten
                  </h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-anthracite">
                    {city.object_types.map((o) => (
                      <li key={o}>· {o}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {city.towns?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                  Auch in der Umgebung
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {city.towns.map((t) => (
                    <span key={t} className="rounded-sm bg-light-gray px-3 py-1.5 text-sm text-anthracite">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Reveal>

          <Reveal delay={1}>
            <div className="grid gap-4">
              <a
                href="tel:+4915170004440"
                className="group flex items-center justify-between rounded-sm bg-yellow p-6 transition-colors hover:bg-yellow-dark"
                data-testid="city-call"
              >
                <span className="flex items-center gap-3">
                  <Phone className="h-6 w-6 text-brand-black" />
                  <span className="font-heading font-bold text-brand-black">0151 70004440</span>
                </span>
                <ArrowRight className="h-5 w-5 text-brand-black transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/privatkunden"
                className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
                data-testid="city-private"
              >
                <span className="flex items-center gap-3">
                  <Users className="h-6 w-6 text-yellow" />
                  <span className="font-heading font-bold text-anthracite">Für Privatkunden</span>
                </span>
                <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/geschaeftskunden"
                className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
                data-testid="city-business"
              >
                <span className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-yellow" />
                  <span className="font-heading font-bold text-anthracite">Für Geschäftskunden</span>
                </span>
                <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
              </Link>
              {city.services?.length > 0 && (
                <div className="rounded-sm border border-light-gray bg-white p-6">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                    Leistungen in {city.name}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {city.services.map((s) => (
                      <span key={s} className="rounded-sm bg-light-gray px-3 py-1.5 text-xs text-anthracite">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Vorher-Nachher */}
      {projects.length > 0 && (
        <section className="bg-light-gray py-16 md:py-24" data-testid="city-projects">
          <div className="container-page">
            <Reveal>
              <span className="kicker">Vorher &amp; Nachher</span>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
                {city.projects_general ? "Beispiele aus unseren Einsätzen" : `Projekte aus ${city.name}`}
              </h2>
              {city.projects_general && (
                <p className="mt-3 max-w-2xl text-sm text-mid-gray" data-testid="city-projects-disclaimer">
                  Hinweis: Die gezeigten Vorher-Nachher-Beispiele stammen aus verschiedenen Einsätzen
                  in unseren Einsatzgebieten und nicht zwingend aus {city.name}.
                </p>
              )}
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((p) => (
                <div key={p.id} className="overflow-hidden rounded-md border border-light-gray bg-white p-4" data-testid={`city-project-${p.id}`}>
                  {p.before_image && p.after_image ? (
                    <BeforeAfterSlider before={p.before_image} after={p.after_image} alt={p.title} />
                  ) : p.after_image ? (
                    <img src={p.after_image} alt={p.alt_after || p.title} className="aspect-[4/3] w-full rounded-sm object-cover" loading="lazy" decoding="async" />
                  ) : null}
                  <div className="p-2 pt-5">
                    <h3 className="font-heading text-lg font-bold text-anthracite">{p.title}</h3>
                    <p className="text-sm text-mid-gray">
                      {[p.object_type, p.service_type, city.projects_general ? p.region : city.name].filter(Boolean).join(" · ")}
                    </p>
                    {p.detail_active && (
                      <Link to={`/referenzen/${p.slug}`} className="mt-3 inline-flex items-center gap-1 font-heading text-sm font-bold text-anthracite hover:text-yellow-dark">
                        Projekt ansehen <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {faq.length > 0 && (
        <section className="bg-white py-16 md:py-24">
          <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <Reveal>
              <span className="kicker">FAQ</span>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
                Fragen zu {city.name}
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <FaqAccordion items={faq} idPrefix={city.slug} />
            </Reveal>
          </div>
        </section>
      )}

      <CtaSection title={`Entrümpelung in ${city.name} anfragen`} />
    </div>
  );
}
